import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { initialApprovals, initialIntegrations, initialModules, workflows as previewWorkflows } from './data'
import { dawnApi, type ApiApproval, type ApiRun, type ApiWorkflow } from './lib/dawnApi'
import type {
  Approval,
  ApprovalStatus,
  HermesRuntimeState,
  IntegrationItem,
  LaunchTaskInput,
  ModuleItem,
  RuntimeMode,
  WorkflowItem,
  WorkflowRunItem,
} from './types'

interface DemoStoreValue {
  approvals: Approval[]
  modules: ModuleItem[]
  integrations: IntegrationItem[]
  workflows: WorkflowItem[]
  runs: WorkflowRunItem[]
  apiMode: RuntimeMode
  apiError: string | null
  hermesState: HermesRuntimeState
  lastRefresh: string | null
  updateApproval: (id: string, status: ApprovalStatus, comment?: string) => Promise<void>
  toggleModule: (id: string) => void
  toggleIntegration: (id: string) => void
  refreshLiveData: () => Promise<void>
  launchWorkflow: (workflowCode: string, task: LaunchTaskInput, estimatedManualMinutes?: number) => Promise<WorkflowRunItem>
}

const DemoStoreContext = createContext<DemoStoreValue | null>(null)

const initialHermesState: HermesRuntimeState = {
  enabled: false,
  configured: false,
  reachable: false,
  errorCode: 'HERMES_STATUS_NOT_LOADED',
}

function titleCase(value: string): string {
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(' ')
}

function parseObject(value: string | null | undefined): Record<string, unknown> | undefined {
  if (!value) return undefined
  try {
    const parsed = JSON.parse(value) as unknown
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed as Record<string, unknown>
      : undefined
  } catch {
    return undefined
  }
}

function optionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

function mapWorkflow(item: ApiWorkflow): WorkflowItem {
  return {
    id: item.code,
    name: item.name,
    outcome: item.outputs.length ? item.outputs.map(titleCase).join(' · ') : titleCase(item.case_study_category),
    module: titleCase(item.module),
    schedule: item.schedule_expression || 'On demand',
    approvals: item.approval_policy.startsWith('not-required') ? 0 : 1,
    estimatedUsage: 'Budget-controlled',
    status: item.enabled ? 'ready' : 'draft',
    version: item.version,
    mode: item.mode,
    risk: item.risk_level,
    releaseStage: item.release_stage,
    inputs: item.inputs,
    outputs: item.outputs,
    metrics: item.metrics,
    caseStudyCategory: item.case_study_category,
  }
}

function mapApprovalStatus(status: ApiApproval['status']): ApprovalStatus {
  if (status === 'approved' || status === 'approved-with-changes') return 'approved'
  if (status === 'returned') return 'returned'
  if (status === 'rejected' || status === 'cancelled' || status === 'expired') return 'rejected'
  return 'pending'
}

function mapRisk(risk: ApiApproval['risk_level']): Approval['risk'] {
  if (risk === 'low') return 'Low'
  if (risk === 'high' || risk === 'critical') return 'High'
  return 'Medium'
}

function mapApproval(item: ApiApproval): Approval {
  return {
    id: item.id,
    type: titleCase(item.approval_type),
    title: item.title,
    summary: item.summary,
    requestedBy: 'DAWN workflow control plane',
    risk: mapRisk(item.risk_level),
    cost: item.estimated_external_cost_gbp > 0
      ? `£${item.estimated_external_cost_gbp.toFixed(2)}`
      : 'No external spend',
    status: mapApprovalStatus(item.status),
    due: item.expires_at
      ? new Date(item.expires_at).toLocaleString('en-GB')
      : `Requested ${new Date(item.requested_at).toLocaleString('en-GB')}`,
    workflowRunId: item.workflow_run_id,
    isLive: true,
  }
}

function mapRun(item: ApiRun, workflowNames: Map<string, string>): WorkflowRunItem {
  const outputJson = parseObject(item.output_json)
  const hermes = outputJson?.hermes && typeof outputJson.hermes === 'object'
    ? outputJson.hermes as Record<string, unknown>
    : undefined
  const failure = outputJson?.failure && typeof outputJson.failure === 'object'
    ? outputJson.failure as Record<string, unknown>
    : undefined
  const agentUsage = parseObject(item.agent_usage_json)
  const embeddedUsage = hermes?.usage && typeof hermes.usage === 'object'
    ? hermes.usage as Record<string, unknown>
    : undefined
  const usage = agentUsage ?? embeddedUsage

  return {
    id: item.id,
    workflowCode: item.workflow_code,
    workflowName: workflowNames.get(item.workflow_code) ?? titleCase(item.workflow_code),
    status: item.status,
    requestedBy: item.requested_by,
    triggerType: item.trigger_type,
    createdAt: item.created_at,
    startedAt: item.started_at,
    finishedAt: item.finished_at,
    durationMs: item.duration_ms,
    usageCostGbp: item.usage_cost_gbp,
    estimatedManualMinutes: item.estimated_manual_minutes,
    verifiedTimeSavedMinutes: item.verified_time_saved_minutes,
    outputText: optionalString(hermes?.output),
    outputJson,
    errorCode: item.error_code ?? optionalString(failure?.code) ?? null,
    errorMessage: item.error_message ?? optionalString(failure?.message) ?? null,
    agentStatus: item.agent_status ?? null,
    agentRunId: item.external_run_id ?? optionalString(hermes?.externalRunId) ?? null,
    agentLastEvent: item.last_event ?? optionalString(hermes?.lastEvent) ?? null,
    approvalCount: item.approval_count ?? optionalNumber(hermes?.approvalCount) ?? 0,
    tokenUsage: usage ? {
      inputTokens: optionalNumber(usage.input_tokens),
      outputTokens: optionalNumber(usage.output_tokens),
      totalTokens: optionalNumber(usage.total_tokens),
    } : undefined,
  }
}

export function DemoStoreProvider({ children }: { children: ReactNode }) {
  const [pilotApprovals, setPilotApprovals] = useState(initialApprovals)
  const [liveApprovals, setLiveApprovals] = useState<Approval[]>([])
  const [modules, setModules] = useState(initialModules)
  const [integrations, setIntegrations] = useState(initialIntegrations)
  const [workflows, setWorkflows] = useState<WorkflowItem[]>(previewWorkflows)
  const [runs, setRuns] = useState<WorkflowRunItem[]>([])
  const [apiMode, setApiMode] = useState<RuntimeMode>('connecting')
  const [apiError, setApiError] = useState<string | null>(null)
  const [hermesState, setHermesState] = useState<HermesRuntimeState>(initialHermesState)
  const [lastRefresh, setLastRefresh] = useState<string | null>(null)

  const refreshLiveData = useCallback(async () => {
    setApiMode(current => current === 'live' ? 'live' : 'connecting')
    setApiError(null)

    try {
      const [health, hermesResponse, workflowResponse, runResponse, approvalResponse] = await Promise.all([
        dawnApi.health(),
        dawnApi.hermes(),
        dawnApi.workflows(),
        dawnApi.runs(),
        dawnApi.approvals(),
      ])

      const mappedWorkflows = workflowResponse.workflows.map(mapWorkflow)
      const workflowNames = new Map(mappedWorkflows.map(item => [item.id, item.name]))

      setWorkflows(mappedWorkflows)
      setRuns(runResponse.runs.map(item => mapRun(item, workflowNames)))
      setLiveApprovals(approvalResponse.approvals.map(mapApproval))
      setHermesState(hermesResponse)
      setApiMode(health.status === 'operational' ? 'live' : 'offline')
      setLastRefresh(new Date().toISOString())
      setIntegrations(items => items.map(item => {
        if (item.id === 'cloudflare') {
          return {
            ...item,
            connected: true,
            owner: 'Local Tenant 001 control plane',
            lastSync: `Connected ${new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`,
          }
        }
        if (item.id === 'hermes') {
          return {
            ...item,
            connected: hermesResponse.reachable,
            owner: hermesResponse.reachable ? 'Private DAWN execution engine' : 'Local setup required',
            lastSync: hermesResponse.reachable
              ? `${hermesResponse.platform || 'Hermes'} · ${hermesResponse.model || 'configured model'}`
              : hermesResponse.errorCode || 'Hermes not connected',
          }
        }
        return item
      }))
    } catch (error) {
      setApiMode('offline')
      setApiError(error instanceof Error ? error.message : 'The local DAWN control plane is unavailable.')
      setHermesState(initialHermesState)
      setIntegrations(items => items.map(item => {
        if (item.id === 'cloudflare') return { ...item, connected: false, owner: 'Deployment gate', lastSync: 'Local Worker not detected' }
        if (item.id === 'hermes') return { ...item, connected: false, owner: 'Local setup required', lastSync: 'DAWN control plane unavailable' }
        return item
      }))
    }
  }, [])

  useEffect(() => {
    void refreshLiveData()
  }, [refreshLiveData])

  const updateApproval = useCallback(async (id: string, status: ApprovalStatus, comment = '') => {
    if (id.startsWith('gate-')) {
      setPilotApprovals(items => items.map(item => item.id === id ? { ...item, status } : item))
      return
    }
    if (status === 'pending') return

    const response = await dawnApi.decideApproval(id, status, comment)
    if (response.workflowNotified === false) {
      setApiError('The approval decision was saved, but the running workflow did not acknowledge the resume event. Check the Worker logs.')
    }
    await refreshLiveData()
  }, [refreshLiveData])

  const launchWorkflow = useCallback(async (
    workflowCode: string,
    task: LaunchTaskInput,
    estimatedManualMinutes = 30,
  ) => {
    try {
      const response = await dawnApi.launchWorkflow(workflowCode, {
        ...task,
        submittedAt: new Date().toISOString(),
        tenantId: 'org_deus_intus_001',
        externalActionsRequested: false,
      }, estimatedManualMinutes)

      await refreshLiveData()
      const workflowNames = new Map(workflows.map(item => [item.id, item.name]))
      return mapRun(response.run, workflowNames)
    } catch (error) {
      setApiError(error instanceof Error ? error.message : 'The workflow could not be queued.')
      throw error
    }
  }, [refreshLiveData, workflows])

  const approvals = useMemo(() => [...pilotApprovals, ...liveApprovals], [pilotApprovals, liveApprovals])

  const value = useMemo<DemoStoreValue>(() => ({
    approvals,
    modules,
    integrations,
    workflows,
    runs,
    apiMode,
    apiError,
    hermesState,
    lastRefresh,
    updateApproval,
    toggleModule: id => setModules(items => items.map(item => item.id === id && item.id !== 'brain'
      ? { ...item, enabled: !item.enabled }
      : item)),
    toggleIntegration: id => setIntegrations(items => items.map(item => item.id === id
      ? {
          ...item,
          connected: !item.connected,
          lastSync: item.connected ? 'Not connected' : 'Connection requires secure setup',
          owner: item.connected ? '—' : 'Pilot configuration',
        }
      : item)),
    refreshLiveData,
    launchWorkflow,
  }), [
    approvals,
    modules,
    integrations,
    workflows,
    runs,
    apiMode,
    apiError,
    hermesState,
    lastRefresh,
    updateApproval,
    refreshLiveData,
    launchWorkflow,
  ])

  return <DemoStoreContext.Provider value={value}>{children}</DemoStoreContext.Provider>
}

export function useDemoStore() {
  const value = useContext(DemoStoreContext)
  if (!value) throw new Error('useDemoStore must be used inside DemoStoreProvider')
  return value
}
